import * as React from 'react';
import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  CollectionPreferences,
  CollectionPreferencesProps,
  CopyToClipboard,
  Modal,
  Pagination,
  SpaceBetween,
  Table,
  TableProps,
  TextContent,
  TextFilter,
} from '@amzn/awsui-components-react-v3';
import { useCollection } from '@amzn/awsui-collection-hooks';
import { dissociateGlossaryFromResource, listResourcesAssociatedToMetadataEntity } from 'src/api/catalog';
import { defaultWrapLinesPreference, largePageSizePreference, paginationLabels } from 'src/commons/tables';
import { convertResourceARNToLink, getScopeForArn } from 'src/components/utils/arnUtil';
import { Link } from 'src/components/common/Link';
import { HC_RESOURCE_ARN_PREFIX, METADATA_FORM_PREFIX } from 'src/commons/constants';
import { EmptyState } from 'src/commons/EmptyState';

import { PageHeader } from 'src/components/common/PageHeader';
import { GlossaryDetails } from 'aws-sdk/clients/awsdlhybridcatalogservicelambda';
import { getDistinctObjects } from 'src/components/workspaces/common/common';
import CsvDownloaderWrapper, { generateCsvColumnDefinitions } from 'src/components/common/csvDownloaderWrapper';
import {
  flattenGlossaryResourceAssociation,
  generateCsvFileName,
  getCsvDataForGlossaryResources,
} from 'src/commons/common';
import _ from 'lodash';
import { getResourceNameFromArn } from 'src/components/dataadvisory/constants';
import { createMetadataFormViewLink } from 'src/routes';

export interface BusinessGlossaryAssociationsTableProps {
  glossary: any;
  childGovernanceTagGlossaries?: GlossaryDetails[];
  enableCSVDownload?: boolean;
  ownerId: string;
  setNotification: (header: any, message: string) => void;
  activeWorkspace?: any;
  isGovernanceTag?: boolean;
}

const MAX_RES_8000 = 8000;
const BusinessGlossaryAssociationsTable = (props: BusinessGlossaryAssociationsTableProps) => {
  const [glossaryAssociations, setGlossaryAssociations] = useState([]);
  const [selectedAssociatedResource, setSelectedAssociatedResource] = useState(undefined);
  const [buttonLoading, setButtonLoading] = useState(false);
  const [loadingGlossaryAssociations, setLoadingGlossaryAssociations] = useState(false);
  const [dissociateGlossaryModalVisible, setDissociateGlossaryModalVisible] = useState(false);
  const [allDataLoaded, setAllDataLoaded] = useState(false);
  const [nextToken, setNextToken] = useState(null);
  const glossaryId = props.glossary.GlossaryId;
  const governanceTagName = props.glossary.GlossaryValue;
  const glossaryOwnerId = props.glossary.OwnerId;
  const childGovernanceTagGlossaries = props.childGovernanceTagGlossaries;
  const enableCSVDownload: boolean = props.enableCSVDownload;
  const isGovernanceTag: boolean = props?.isGovernanceTag;
  const [preferences, setPreferences] = useState<CollectionPreferencesProps.Preferences>({
    wrapLines: false,
    pageSize: 25,
  });
  const [csvColumnDefinition, setCsvColumnDefinition] = useState([]);
  const [csvData, setCsvData] = useState([]);

  const refForCSVDownloader: React.RefObject<any> = React.createRef();
  const onClickDownload = () => {
    refForCSVDownloader.current.handleClick();
  };

  const { items, collectionProps, paginationProps, filterProps, filteredItemsCount } = useCollection(
    glossaryAssociations == undefined ? [] : glossaryAssociations,
    {
      filtering: {
        noMatch: '',
        empty: <EmptyState title={'No associations'} subtitle={'No associated resources were found.'} />,
      },
      pagination: { pageSize: preferences.pageSize },
      sorting: {},
      selection: {},
      propertyFiltering: {
        filteringProperties: [],
      },
    },
  );

  const generateResourceARNLink = (item: any) => {
    const arn = item.Resource;
    if (arn.startsWith(METADATA_FORM_PREFIX)) {
      return createMetadataFormViewLink(arn, item.OwnerId);
    }
    if (arn.startsWith(HC_RESOURCE_ARN_PREFIX)) {
      return convertResourceARNToLink(item.Resource);
    }
  };

  const glossaryAssociationColumnDefinitions: TableProps.ColumnDefinition<any>[] = [
    {
      id: 'resourceName',
      header: 'Resource name',
      cell: (item) => <Link href={generateResourceARNLink(item)}>{getResourceNameFromArn(item.Resource)}</Link>,
      minWidth: 150,
      sortingField: 'ResourceName',
    },
    {
      id: 'resourceArn',
      header: 'Resource ARN',
      cell: (item) => (
        <CopyToClipboard
          textToCopy={item.Resource}
          variant={'inline'}
          copySuccessText={'Successfully copied ARN'}
          copyErrorText={'Failed to copy ARN'}
        />
      ),
      minWidth: 300,
      sortingField: 'Resource',
    },
    {
      id: 'resourceType',
      header: 'Resource type',
      cell: (item) => {
        if (item.Resource.startsWith(METADATA_FORM_PREFIX)) {
          return 'Metadata form';
        }
        return getScopeForArn(item.Resource);
      },
      minWidth: 200,
    },
  ];

  useEffect(() => {
    const { selectedItems } = collectionProps;
    const selectedGlossaryAssociation = selectedItems[0];
    if (selectedGlossaryAssociation) {
      setSelectedAssociatedResource(selectedGlossaryAssociation);
    }
  }, [collectionProps.selectedItems]);

  useEffect(() => {
    getGlossaryAssociations();
  }, [glossaryId]);

  const getGlossaryAssociationsPage = async (
    glossaryId: string,
    glossaryOwnerId: string,
    nextToken: string | undefined,
    maxResults: number,
  ): Promise<{ associations: any[]; nextToken: string }> => {
    let glossaryAssociations = [];
    let result = await listResourcesAssociatedToMetadataEntity({
      OwnerId: glossaryOwnerId,
      Id: glossaryId,
      NextToken: nextToken,
      MaxResults: maxResults,
    });
    glossaryAssociations.push(
      ...result.FormList,
      ...result.TableList,
      ...result.CatalogList,
      ...result.DatabaseList,
      ...result.ClusterList,
      ...result.ColumnList,
      ...result.SchemaList,
      ...result.FineGrainAccessPolicyList,
    );
    return {
      associations: glossaryAssociations,
      nextToken: result.NextToken,
    };
  };

  const getGlossaryAssociations = async () => {
    setLoadingGlossaryAssociations(true);
    setAllDataLoaded(false);
    setGlossaryAssociations([]);
    try {
      // get all glossaries for owner and filter on the results
      let assns = [];
      if (childGovernanceTagGlossaries) {
        for (const childGovernanceTag of childGovernanceTagGlossaries) {
          // fetch all resource associations of childGovernanceTag
          let localNextToken = null;
          do {
            const { associations, nextToken: nextTokeRes } = await getGlossaryAssociationsPage(
              childGovernanceTag.GlossaryId,
              childGovernanceTag.OwnerId,
              localNextToken,
              MAX_RES_8000,
            );
            assns.push(...associations);
            localNextToken = nextTokeRes;
          } while (!!localNextToken);
        }
      }
      // paginate and fetch using nextToken until one result is available.  Else the first page shows 0, but on nextPage fetch it shows
      // results
      let localNextToken = null;
      do {
        const { associations, nextToken: nextTokeRes } = await getGlossaryAssociationsPage(
          glossaryId,
          glossaryOwnerId,
          localNextToken,
          MAX_RES_8000,
        );
        assns.push(...associations);
        setNextToken(nextTokeRes);
        localNextToken = nextTokeRes;
        // loop for all resource associations for governance tags, but stop for first page for business glossaries.
      } while (isGovernanceTag ? localNextToken != null : assns.length == 0 && localNextToken != null);

      if (localNextToken == null) {
        setAllDataLoaded(true);
      }
      let distinctAssociations = getDistinctObjects(assns, 'Resource');
      setGlossaryAssociations(distinctAssociations);
      prepareCSVDownloadData(distinctAssociations);
    } catch (err) {
      props.setNotification('error', `Unable to load glossaries. ${err.message}`);
    } finally {
      setLoadingGlossaryAssociations(false);
      setSelectedAssociatedResource(undefined);
    }
  };

  const prepareCSVDownloadData = (distinctAssociations: any[]) => {
    if (enableCSVDownload) {
      let csvData = distinctAssociations
        .map((item) => flattenGlossaryResourceAssociation(item))
        .filter((item) => !_.isEmpty(item))
        .map((item) => getCsvDataForGlossaryResources(item));
      setCsvData(csvData);
      setCsvColumnDefinition(generateCsvColumnDefinitions(csvData));
    }
  };

  const loadMoreGlossaryAssociations = async () => {
    setLoadingGlossaryAssociations(true);
    setGlossaryAssociations([]);
    try {
      const { associations, nextToken: nextTokeRes } = await getGlossaryAssociationsPage(
        glossaryId,
        glossaryOwnerId,
        nextToken,
        MAX_RES_8000,
      );
      let currentGlossaryAssociations = glossaryAssociations;
      currentGlossaryAssociations.push(...associations);
      setGlossaryAssociations(getDistinctObjects(currentGlossaryAssociations, 'Resource'));
      setNextToken(nextTokeRes);
      if (nextTokeRes == null) {
        setAllDataLoaded(true);
      }
    } catch (err) {
      props.setNotification('error', `Unable to load glossaries. ${err.message}`);
    } finally {
      setLoadingGlossaryAssociations(false);
      setSelectedAssociatedResource(undefined);
    }
  };

  const handlePageChange = async (e) => {
    if (paginationProps.currentPageIndex >= paginationProps.pagesCount && !allDataLoaded) {
      await loadMoreGlossaryAssociations();
    }
    if (
      paginationProps.currentPageIndex != paginationProps.pagesCount ||
      (e.detail.currentPageIndex <= paginationProps.pagesCount &&
        e.detail.currentPageIndex != paginationProps.currentPageIndex)
    ) {
      paginationProps.onChange(e);
    }
  };

  const handleDissociateGlossaryFromResource = async () => {
    setButtonLoading(true);
    try {
      let dissociateGlossaryResult = await dissociateGlossaryFromResource({
        ResourceArn: selectedAssociatedResource?.Resource,
        GlossaryId: selectedAssociatedResource?.GlossaryId,
      });
      if (dissociateGlossaryResult.Message == 'Success') {
        props.setNotification('success', 'Success');
      } else {
        props.setNotification('error', `Failure in dissociating glossary. Reason:${dissociateGlossaryResult.Message}`);
      }
    } catch (err) {
      props.setNotification('error', `Error in dissociating glossary:${err.message}`);
    } finally {
      setButtonLoading(false);
      setDissociateGlossaryModalVisible(false);
      await getGlossaryAssociations();
    }
  };

  const glossaryAssociationTableButtonOptions = () => {
    return [
      {
        text: '',
        icon: 'refresh',
        onItemClick: getGlossaryAssociations,
      },
      {
        text: 'Dissociate',
        variant: 'primary',
        disabled:
          !selectedAssociatedResource ||
          (selectedAssociatedResource && selectedAssociatedResource.GlossaryId !== glossaryId),
        onItemClick: () => setDissociateGlossaryModalVisible(true),
      },
    ];
  };

  const getFilterCounterText = (count) => `${count} ${count === 1 ? 'match' : 'matches'}`;

  return (
    <>
      <Modal
        visible={dissociateGlossaryModalVisible}
        header={[`Confirm deletion`]}
        onDismiss={() => setDissociateGlossaryModalVisible(false)}
        footer={
          <Box float='right'>
            <SpaceBetween direction='horizontal' size='xs'>
              <Button variant='primary' loading={buttonLoading} onClick={handleDissociateGlossaryFromResource}>
                Confirm
              </Button>
            </SpaceBetween>
          </Box>
        }
      >
        <TextContent>
          Are you sure you want to dissociate from resource <strong>{selectedAssociatedResource?.Resource}</strong>?
          <br />
        </TextContent>
      </Modal>
      <SpaceBetween size='l'>
        <Table
          {...collectionProps}
          selectionType='single'
          loadingText='Loading glossaries...'
          loading={loadingGlossaryAssociations}
          columnDefinitions={glossaryAssociationColumnDefinitions}
          items={items}
          wrapLines={preferences.wrapLines}
          empty={
            <Box textAlign='center' color='inherit'>
              <b>No associations</b>
              <Box variant='p' color='inherit'>
                No associations to display.
              </Box>
            </Box>
          }
          filter={
            <TextFilter
              {...filterProps}
              filteringAriaLabel='Filter resources'
              filteringPlaceholder='Find resources'
              disabled={loadingGlossaryAssociations}
              countText={getFilterCounterText(filteredItemsCount)}
            />
          }
          resizableColumns={true}
          header={
            <PageHeader
              counter={`(${glossaryAssociations.length}${nextToken ? '+' : ''})`}
              buttons={glossaryAssociationTableButtonOptions()}
              header={'Resource associations'}
              additionalItems={
                enableCSVDownload &&
                !loadingGlossaryAssociations && (
                  <>
                    <Button
                      variant='primary'
                      disabled={!csvData || csvData.length == 0}
                      onClick={onClickDownload}
                      iconName='download'
                    >
                      Download as CSV
                    </Button>
                    <CsvDownloaderWrapper
                      rowItems={csvData}
                      fileName={generateCsvFileName(`governance-tag-${governanceTagName}`, null)}
                      refForCsvDownloader={refForCSVDownloader}
                      userFriendlyDataDefinitions={csvColumnDefinition}
                    />
                  </>
                )
              }
            />
          }
          preferences={
            <CollectionPreferences
              title={'Preferences'}
              confirmLabel={'Confirm'}
              cancelLabel={'Cancel'}
              preferences={preferences}
              onConfirm={({ detail }) => setPreferences(detail)}
              pageSizePreference={largePageSizePreference}
              wrapLinesPreference={defaultWrapLinesPreference}
            />
          }
          pagination={
            <Pagination
              {...paginationProps}
              ariaLabels={paginationLabels}
              onChange={(e) => handlePageChange(e)}
              openEnd={!allDataLoaded}
              disabled={loadingGlossaryAssociations}
            />
          }
        />
      </SpaceBetween>
    </>
  );
};

export default BusinessGlossaryAssociationsTable;
